import { makeAutoObservable } from 'mobx';
import { FilterJSON, AssetFilterMandatoryParams } from '../modules/projectView/models/filtersAndSorts/types';
import FilterOption from '../modules/projectView/models/filtersAndSorts/filterOption';
import { filterOptionFromJson } from '../modules/projectView/models/filtersAndSorts/filterOptionFactory';
import { trackChangedAssetsFilter } from '../../services/tracker/trackers/assets';

type FilterSerialization = {
	searchText: string,
	filterOptions: FilterJSON[],
	isFilterSidePanelOpen: boolean,
	collapsedFilters: string[]
}

export type FilterStoreSerialization = {
	[projectID: number]: FilterSerialization
}

export type FilterState = {
	[projectID: number]: AssetFilterMandatoryParams;
}

export default class FilterStore {
	filterState: FilterState;
	private NEW_CONFIGURATION_TEMPLATE = {
		searchText: '',
		filterOptions: [] as FilterOption[],
		isFilterSidePanelOpen: false,
		collapsedFilters: [] as string[]
	};

	constructor( filterState: FilterState = {} ) {
		this.filterState = filterState;

		makeAutoObservable( this );
	}

	serialize(): FilterStoreSerialization {
		const serialization: FilterStoreSerialization = {};

		Object.entries( this.filterState ).forEach(
			( [ projectID, filterParams ] : [ string, AssetFilterMandatoryParams ] ) => {
				serialization[ Number( projectID ) ] = {
					searchText: filterParams.searchText,
					filterOptions: filterParams.filterOptions.map( option => option.toJson() ),
					isFilterSidePanelOpen: filterParams.isFilterSidePanelOpen,
					collapsedFilters: filterParams.collapsedFilters
				};
			}
		);

		return serialization;
	}

	hydrate( serialization: FilterStoreSerialization ) {
		Object.entries( serialization ).forEach( ( [ projectID, filterParams ] : [ string, FilterSerialization ] ) => {
			this.filterState[ Number( projectID ) ] = {
				searchText: filterParams.searchText,
				filterOptions: filterParams.filterOptions
					.map( option => filterOptionFromJson( option ) )
					.filter( ( option ): option is FilterOption => option !== null ),
				isFilterSidePanelOpen: filterParams.isFilterSidePanelOpen ?? false,
				collapsedFilters: filterParams.collapsedFilters ?? []
			};
		} );
	}

	clear() {
		this.filterState = {};
	}

	addProjectFilterNewConfiguration( projectID: number ) {
		this.filterState[ projectID ] = { ...this.NEW_CONFIGURATION_TEMPLATE };

		return this.filterState[ projectID ];
	}

	projectFilterConfigurationIsSet( projectID: number ) {
		return !!this.filterState[ projectID ];
	}

	getProjectFilterConfiguration( projectID: number ) {
		if ( this.projectFilterConfigurationIsSet( projectID ) ) {
			return this.filterState[ projectID ];
		}

		return this.addProjectFilterNewConfiguration( projectID );
	}

	setProjectFilterSearchText( projectID: number, searchText: string ) {
		this.filterState[ projectID ].searchText = searchText;
	}

	getProjectFilterOptions( projectID: number ) {
		const projectFilterConfiguration = this.getProjectFilterConfiguration( projectID );
		return projectFilterConfiguration.filterOptions;
	}

	setProjectFilterOptions( projectID: number, filterOptions: FilterOption[] ) {
		trackChangedAssetsFilter( projectID, filterOptions.map( option => option.id ) );
		this.filterState[ projectID ].filterOptions = filterOptions;
	}

	setProjectFilterSidePanelOpen( projectID: number, isOpen: boolean ) {
		this.filterState[ projectID ].isFilterSidePanelOpen = isOpen;
	}

	getProjectCollapsedFilters( projectID: number ) {
		return this.getProjectFilterConfiguration( projectID ).collapsedFilters;
	}

	setProjectCollapsedFilters( projectID: number, collapsedFilters: string[] ) {
		this.filterState[ projectID ].collapsedFilters = collapsedFilters;
	}
}
